import { z } from 'zod';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { EntityRef, OwnerRef } from './read-models';

/**
 * Strategy read-models — the objective cascade and the roadmap.
 *
 * Two screens, one argument. This product could already prove that a *product*
 * met its objectives, with evidence, through the outcome trace. It could not
 * show that the *portfolio* met the enterprise's, because `core.okr` had no
 * level, no parent and no target: every objective in it belonged to a product.
 * Migration 0081 gave objectives altitude; these are the shapes the screens
 * bind to.
 *
 * Three rules live in the types rather than in the screens that read them.
 *
 * **Progress is nullable, and a null is explained.** An objective whose
 * children carry no measurable key result has `progress: null` and a
 * `contribution.reason` saying why. It is never zero. Zero means the objective
 * is failing; null means nobody has measured it, and an executive reading the
 * first when the second is true will cancel the wrong thing.
 *
 * **A target and a progress figure are different claims and never combine.**
 * `target` is what somebody committed to. `progress` is the mean attainment of
 * the key results traced beneath the objective. Neither is the other's
 * denominator, and the screen shows them side by side rather than dividing one
 * into the other.
 *
 * **A roadmap item with no date is listed, not dropped.** `RoadmapItem.start`
 * is nullable and `undatedReason` says why. A timeline that silently omits what
 * it cannot place reports a smaller plan than exists.
 */

// ─────────────────────────────────────────────────────────────────────────────
// 1. Objectives — /v2/objectives
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The altitudes an objective can be set at, from `ref.objective_level`.
 *
 * The code equals the entity type the objective is anchored to, which is what
 * lets the database check the anchor against the level without a mapping
 * table — see OB-001 in migration 0081.
 */
export const ObjectiveLevel = z.enum([
  'Enterprise',
  'OrganizationalUnit',
  'ValueStream',
  'ProductGroup',
  'Product',
  'Team',
]);
export type ObjectiveLevel = z.infer<typeof ObjectiveLevel>;

/** Draft is somebody thinking. Published is a commitment. Closed is history. */
export const ObjectiveStatus = z.enum(['Draft', 'Published', 'Closed']);
export type ObjectiveStatus = z.infer<typeof ObjectiveStatus>;

/**
 * Whether a commitment is being kept, judged against the period it runs over.
 *
 * `NotTracked` is not a fourth degree of bad. It means no judgement was
 * possible — nothing beneath the objective carries a figure, or the objective
 * is still a draft and therefore not yet something to be held to. An objective
 * nobody has measured must never read `OffTrack`: the first says go and ask,
 * the second says go and intervene, and they are opposite instructions.
 */
export const ObjectiveBand = z.enum(['OnTrack', 'AtRisk', 'OffTrack', 'NotTracked']);
export type ObjectiveBand = z.infer<typeof ObjectiveBand>;

/**
 * The band, the words for it, and the two numbers it was derived from.
 *
 * This is the one figure on the screen nobody recorded — it is inferred, by
 * comparing how far an objective has got against how much of its period has
 * gone. So it travels with its inputs. A reader who disagrees with the call
 * can see both numbers and make their own, which is the difference between a
 * derived status and an asserted one.
 */
export const ObjectiveHealth = z.object({
  band: ObjectiveBand,
  /** The band in words. Status is never carried by colour alone. */
  label: z.string(),
  /**
   * How much of the objective's period had gone at the as-of date, 0–1.
   *
   * Present even when the band is `NotTracked`, because "nine months gone and
   * nothing measured" is a sharper statement than either half alone.
   */
  periodElapsed: z.number().min(0).max(1),
  /** Why this band, naming both numbers behind it. Always present. */
  reason: z.string(),
});
export type ObjectiveHealth = z.infer<typeof ObjectiveHealth>;

/**
 * The target in force, and when it took effect.
 *
 * Every target an objective has ever carried is kept; this is the latest one
 * not in the future. `supersededCount` is how many earlier targets exist, so a
 * reader can see that the number in front of them is the third version without
 * having to open a history they may not need.
 */
export const ObjectiveTarget = z.object({
  value: z.number(),
  unit: z.string(),
  /** What the target is a target *of*. A unit alone does not say. */
  measureLabel: z.string(),
  effectiveFrom: IsoDate,
  /** Why this number. Null where whoever set it did not say. */
  basis: z.string().nullable(),
  setBy: z.string().nullable(),
  setAt: Timestamp,
  supersededCount: z.number().int().nonnegative(),
});
export type ObjectiveTarget = z.infer<typeof ObjectiveTarget>;

/**
 * A key result hanging off an objective, and how far it has got.
 *
 * `attainment` is null — not zero — where the key result carries no target, or
 * where the outcome trace has recorded that it cannot be measured. The reason
 * travels with it, in the words of whoever recorded it.
 */
export const ObjectiveKeyResult = z.object({
  id: Uuid,
  statement: z.string(),
  outcomeType: z.string(),
  /**
   * Where the measure stood before anybody set out to move it.
   *
   * Present only for the traced population, and the reason attainment can be
   * computed for a measure that improves by going down. Null for a key result
   * that carries a target and a current value but no starting point.
   */
  baselineValue: z.number().nullable(),
  targetValue: z.number().nullable(),
  actualValue: z.number().nullable(),
  unit: z.string().nullable(),
  /**
   * How far this key result has travelled, 0–1, or null with the reason.
   *
   * Distance from the baseline toward the target where a baseline exists;
   * current ÷ target where one does not. Two formulas because there are two
   * kinds of record, and one of them describes measures that improve downward.
   */
  attainment: z.number().min(0).max(1).nullable(),
  measurable: z.boolean(),
  unmeasurableReason: z.string().nullable(),
  route: z.string().nullable(),
  /**
   * True where the reading comes from the outcome trace rather than this row.
   *
   * The traced population carries a baseline, a measurement window, delivery
   * links and evidence, and its figures are maintained there. Editing the
   * target or current value held here would change nothing a reader can see,
   * so the screen offers no edit on a traced key result and says why instead
   * of appearing to accept a change it would then ignore.
   */
  traced: z.boolean(),
});
export type ObjectiveKeyResult = z.infer<typeof ObjectiveKeyResult>;

/**
 * What the roll-up counted, and what it left out.
 *
 * Carried on every node so the figure never travels without its coverage. An
 * objective reporting 71% over two of its nine children is a different
 * statement from one reporting 71% over all nine, and only this says which.
 */
export const ObjectiveContribution = z.object({
  measurable: z.boolean(),
  /*
    No `method` here, deliberately.

    It used to carry the same four-hundred-character sentence as
    `ObjectiveCascadeView.method`, byte for byte, on every node — 92KB of one
    repeated paragraph across two hundred objectives, 18% of the response, and
    nothing ever read it. The method is a property of the roll-up, not of each
    node that went through it, and it is stated once at the top of the screen
    where a reader looks for it.
  */
  /** Key results and child objectives that contributed a figure. */
  contributingCount: z.number().int().nonnegative(),
  /** Those that could not, and are therefore not averaged in as zero. */
  excludedCount: z.number().int().nonnegative(),
  /** Present whenever `measurable` is false. Says what would fix it. */
  reason: z.string().nullable(),
});
export type ObjectiveContribution = z.infer<typeof ObjectiveContribution>;

/**
 * The strategy statement the objective's subject already carries, if any.
 *
 * The layer above the objectives in the cascade, and it is read rather than
 * authored here: a product's mission, a product group's strategy, a value
 * stream's purpose are properties of those things, not of the OKR feature.
 *
 * `statement` is nullable and `absentReason` says which kind of absence it is,
 * because there are two and they call for opposite responses. A level that has
 * nowhere to record a strategy is a modelling gap; a level that has the column
 * and has left it empty is somebody's unwritten paragraph. Reporting both as a
 * blank would hide the second, which is the one an owner can fix today.
 */
export const ObjectiveStrategy = z.object({
  statement: z.string().nullable(),
  /** Which field it came from, in the reader's words. Null where none exists. */
  source: z.string().nullable(),
  /** Present whenever `statement` is null. Never a bare blank. */
  absentReason: z.string().nullable(),
});
export type ObjectiveStrategy = z.infer<typeof ObjectiveStrategy>;

export const ObjectiveNode = z.object({
  id: Uuid,
  businessCode: z.string().nullable(),
  /** The objective statement. What somebody committed to, in their words. */
  objective: z.string(),
  level: ObjectiveLevel,
  status: ObjectiveStatus,
  /** The part of the business it is set for. Null where nothing anchors it. */
  subject: EntityRef.nullable(),
  /** What that part of the business says it is for. The layer above the OKR. */
  strategy: ObjectiveStrategy,
  owner: OwnerRef.nullable(),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  parentId: Uuid.nullable(),
  /**
   * False when the objective has a parent this caller cannot reach.
   *
   * The node then renders at the top of the cascade with that stated, rather
   * than appearing to be a root objective — which would tell a product owner
   * their product's objective answers to nobody.
   */
  parentVisible: z.boolean(),
  target: ObjectiveTarget.nullable(),
  keyResults: z.array(ObjectiveKeyResult),
  /** 0–1, or null where nothing beneath it can be measured. Never zero for null. */
  progress: z.number().min(0).max(1).nullable(),
  contribution: ObjectiveContribution,
  /** On track, at risk, off track — or not tracked, which is not a worse third. */
  health: ObjectiveHealth,
  /**
   * Risks somebody has judged to threaten this objective.
   *
   * The band says a commitment is behind; this says what is in its way, with a
   * name attached. Empty means nobody has made the link — not that nothing
   * threatens it.
   */
  threatenedBy: z.array(
    z.object({
      id: Uuid,
      title: z.string(),
      severityBand: z.string(),
      status: z.string(),
      owner: z.string().nullable(),
    }),
  ),
  childCount: z.number().int().nonnegative(),
});
export type ObjectiveNode = z.infer<typeof ObjectiveNode>;

export const ObjectiveCascadeView = z.object({
  /** Flat, parent-before-child. The screen builds the tree from `parentId`. */
  nodes: z.array(ObjectiveNode),
  totals: z.object({
    objectives: z.number().int().nonnegative(),
    published: z.number().int().nonnegative(),
    drafts: z.number().int().nonnegative(),
    /** Objectives whose contribution cannot be measured at all. */
    unmeasurable: z.number().int().nonnegative(),
    /** Objectives carrying no target. Not a fault; a state worth counting. */
    withoutTarget: z.number().int().nonnegative(),
    /**
     * The cascade by band, so the top of the screen answers the only question
     * an executive opens it with. Counted over every objective in view,
     * including drafts — which fall in `notTracked`, never in `offTrack`.
     */
    onTrack: z.number().int().nonnegative(),
    atRisk: z.number().int().nonnegative(),
    offTrack: z.number().int().nonnegative(),
    notTracked: z.number().int().nonnegative(),
    /**
     * Objectives whose subject records no strategy statement.
     *
     * Counted rather than left to be noticed one node at a time. In this
     * estate the value stream and product group narrative columns are empty
     * across the board, and a strategy layer that renders 32 silent blanks
     * would read as a feature that does not work rather than as an estate
     * with nothing written in it yet.
     */
    withoutStrategy: z.number().int().nonnegative(),
  }),
  /** How the roll-up works, said once at the top of the screen. */
  method: z.string(),
  /** How on track / at risk / off track were decided. Stated, not assumed. */
  bandMethod: z.string(),
  /**
   * The date the bands were judged at, which is the as-of date in force.
   *
   * Carried so a reader looking at a back-dated view is not told an objective
   * is behind against today's calendar.
   */
  asOf: IsoDate,
  /**
   * The period the cascade was narrowed to, and what that excluded.
   *
   * An objective is in view when its own period overlaps this one. Stated
   * rather than silent: a filtered tree that does not say it is filtered
   * reports a smaller portfolio than exists.
   */
  period: z.object({
    start: IsoDate,
    end: IsoDate,
    /** Objectives excluded because their period lies outside this one. */
    excludedCount: z.number().int().nonnegative(),
  }),
});
export type ObjectiveCascadeView = z.infer<typeof ObjectiveCascadeView>;

/**
 * Something an objective can be set for, and whether one already is.
 *
 * The picker is served rather than derived in the browser, and that is
 * deliberate: the list is scoped server-side, so an anchor missing from it is
 * one this caller may not set an objective for. A picker built from whatever
 * the screen happened to have loaded would offer choices the write then
 * refuses, which reads as a broken form rather than as an entitlement.
 */
/**
 * One target an objective has carried, and whether it is the one in force.
 *
 * `core.objective_target` never updates a row — `pvi_app` holds INSERT and not
 * UPDATE precisely so a target stated last quarter cannot be quietly rewritten.
 * That makes the history real rather than aspirational, and this is the shape
 * that lets a screen show it.
 *
 * The interesting fact about a target is usually that it moved. A reader
 * looking at "12% by December" wants to know it was 20% in January, and no
 * amount of detail about the current number substitutes for that.
 */
export const ObjectiveTargetRecord = z.object({
  value: z.number(),
  unit: z.string(),
  measureLabel: z.string(),
  effectiveFrom: IsoDate,
  basis: z.string().nullable(),
  setBy: z.string().nullable(),
  setAt: Timestamp,
  /** True for the latest target not in the future — the one being measured against. */
  current: z.boolean(),
  /** True where it takes effect after today and is therefore not yet in force. */
  future: z.boolean(),
});
export type ObjectiveTargetRecord = z.infer<typeof ObjectiveTargetRecord>;

export const ObjectiveTargetHistoryView = z.object({
  objectiveId: Uuid,
  targets: z.array(ObjectiveTargetRecord),
});
export type ObjectiveTargetHistoryView = z.infer<typeof ObjectiveTargetHistoryView>;

export const ObjectiveAnchorOption = z.object({
  id: Uuid,
  businessCode: z.string().nullable(),
  name: z.string(),
  level: ObjectiveLevel,
  /** True where this part of the business already carries an objective. */
  hasObjective: z.boolean(),
});
export type ObjectiveAnchorOption = z.infer<typeof ObjectiveAnchorOption>;

export const ObjectiveAnchorsView = z.object({
  anchors: z.array(ObjectiveAnchorOption),
  /**
   * People this caller can name as an owner.
   *
   * Served with the anchors rather than fetched separately: an objective
   * cannot be published without an owner, so the picker has to be present
   * wherever the objective is authored, and a rule with no affordance beside
   * it is a trap rather than a guard.
   */
  owners: z.array(
    z.object({
      id: Uuid,
      displayName: z.string(),
      /** Email — the catalogue has no job title, and two people share a name. */
      jobTitle: z.string().nullable(),
    }),
  ),
  /** Objectives this caller could cascade a new one from. */
  parents: z.array(
    z.object({
      id: Uuid,
      businessCode: z.string().nullable(),
      objective: z.string(),
      level: ObjectiveLevel,
      subjectName: z.string().nullable(),
    }),
  ),
});
export type ObjectiveAnchorsView = z.infer<typeof ObjectiveAnchorsView>;

// ─────────────────────────────────────────────────────────────────────────────
// 2. Roadmap — /v2/roadmap
// ─────────────────────────────────────────────────────────────────────────────

/** What a bar on the roadmap is. Nothing here is authored for the roadmap. */
export const RoadmapItemKind = z.enum(['Release', 'Objective']);
export type RoadmapItemKind = z.infer<typeof RoadmapItemKind>;

export const RoadmapGrouping = z.enum(['product', 'productGroup', 'valueStream', 'objective']);
export type RoadmapGrouping = z.infer<typeof RoadmapGrouping>;

/**
 * One item on the timeline.
 *
 * `start` is nullable and that is the whole point of the type. A release with
 * no planned date is real and belongs on the plan; placing it at an arbitrary
 * point would invent a commitment, and dropping it would report a smaller plan
 * than exists. It is listed as undated with the reason stated.
 */
export const RoadmapItem = z.object({
  id: Uuid,
  kind: RoadmapItemKind,
  businessCode: z.string().nullable(),
  name: z.string(),
  /** Null for an undated item. */
  start: IsoDate.nullable(),
  /** Null for a point-in-time item such as a release. */
  end: IsoDate.nullable(),
  /** True when the date is what actually happened rather than what was planned. */
  actual: z.boolean(),
  undatedReason: z.string().nullable(),
  groupId: z.string(),
  groupLabel: z.string(),
  route: z.string().nullable(),
});
export type RoadmapItem = z.infer<typeof RoadmapItem>;

export const RoadmapGroup = z.object({
  id: z.string(),
  label: z.string(),
  entityType: z.string().nullable(),
  route: z.string().nullable(),
  itemCount: z.number().int().nonnegative(),
  undatedCount: z.number().int().nonnegative(),
});
export type RoadmapGroup = z.infer<typeof RoadmapGroup>;

export const RoadmapView = z.object({
  groupBy: RoadmapGrouping,
  /** The window the timeline draws. Derived from the items, not from today. */
  windowStart: IsoDate.nullable(),
  windowEnd: IsoDate.nullable(),
  groups: z.array(RoadmapGroup),
  /** Everything that could be placed on the timeline. */
  items: z.array(RoadmapItem),
  /** Everything that could not, listed rather than dropped. */
  undated: z.array(RoadmapItem),
  totals: z.object({
    placed: z.number().int().nonnegative(),
    undated: z.number().int().nonnegative(),
    releases: z.number().int().nonnegative(),
    objectives: z.number().int().nonnegative(),
  }),
});
export type RoadmapView = z.infer<typeof RoadmapView>;
